/** Display helpers: dates render in the user's locale; the service day is computed in America/New_York. */
import { SERVICE_TZ, keyToNoonUtc, serviceDateKey } from '@shared/dates';

export { serviceDateKey };

export function localeFor(lang: string): string {
  return lang === 'es' ? 'es-US' : 'en-US';
}

/** "Wednesday, September 30" for a civil day key — formatted at noon UTC in UTC so the day never shifts. */
export function formatDayKey(key: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(keyToNoonUtc(key)),
  );
}

/** "Wed, Oct 28" (short, for a line that lists times). */
export function formatDayKeyShort(key: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(key)));
}

export function formatInstant(iso: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { dateStyle: 'medium', timeStyle: 'short', timeZone: SERVICE_TZ }).format(new Date(iso));
}

/** The hour in New York, for "Good morning / afternoon / evening". */
export function serviceHour(now: Date = new Date()): number {
  return Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: SERVICE_TZ }).format(now));
}

export function greetingKey(now: Date = new Date()): 'today.morning' | 'today.afternoon' | 'today.evening' {
  const h = serviceHour(now);
  return h < 12 ? 'today.morning' : h < 17 ? 'today.afternoon' : 'today.evening';
}

/** "Wed, Sep 30" */
export function formatDayShort(key: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(key)));
}

/** "Sep 30" */
export function formatMonthDay(key: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(key)));
}

/** "Sep 30, 2026" */
export function formatDayMedium(key: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(key)));
}

/** "Wed" */
export function formatWeekdayShort(key: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { weekday: 'short', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(key)));
}

/** "October 2026" */
export function formatMonthYear(key: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(key)));
}

/** "4:10 PM" for an instant, on the service clock. */
export function formatClockOf(iso: string, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { hour: 'numeric', minute: '2-digit', timeZone: SERVICE_TZ }).format(new Date(iso));
}

/** "hh:mm" for "Updated hh:mm" — the viewer's own clock. */
export function formatUpdated(ms: number, lang: string): string {
  return new Intl.DateTimeFormat(localeFor(lang), { hour: 'numeric', minute: '2-digit' }).format(new Date(ms));
}
